/**
 * Data hooks for the Phase 0 and 1 screens (docs/mobile-design.md §7): session, notifications, people, search, the chat
 * list, my shifts, the time clock and my runs. Each takes the workspace id explicitly; the app decides which one is
 * current. They only fetch and cache: no DOM, so the same hooks serve React DOM and React Native.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dataApi } from './client';
import type { GeoReading, LeaveBookingInput } from '@tradeoutlet/shared';
import { qk } from './keys';

type Ws = string | null | undefined;

// ---- me / session
export function useMeQuery(enabled: boolean) {
  return useQuery({ queryKey: qk.me, queryFn: () => dataApi().me(), enabled, staleTime: 5 * 60_000 });
}

// ---- notifications
export function useNotifications(ws: Ws, o: { limit?: number } = {}) {
  return useQuery({ queryKey: qk.notifications(ws ?? ''), queryFn: () => dataApi().notifications(ws!, { limit: o.limit ?? 50 }), enabled: Boolean(ws) });
}
export function useMarkNotificationRead(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dataApi().markNotificationRead(id),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notifications(ws ?? '') }),
  });
}
export function useMarkAllNotificationsRead(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dataApi().markAllNotificationsRead(ws!),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notifications(ws ?? '') }),
  });
}

// ---- people
export function usePeopleDirectory(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.peopleDirectory(ws ?? null), queryFn: () => dataApi().peopleDirectory(ws!), enabled: Boolean(ws) && enabled, staleTime: 5 * 60_000 });
}
export function usePersonProfile(ws: Ws, id: string | null | undefined) {
  return useQuery({ queryKey: qk.person(ws ?? null, id ?? null), queryFn: () => dataApi().personProfile(ws!, id!), enabled: Boolean(ws && id) });
}

// ---- search
export type SearchType = 'all' | import('@tradeoutlet/shared').SearchKind;
/** t303u: sort, dates, From and In travel with the query (the type is the tab). */
export type SearchFilters = Omit<import('@tradeoutlet/shared').SearchOptions, 'type' | 'offset' | 'limit'>;
export function useSearch(ws: Ws, q: string, type: SearchType = 'all', filters: SearchFilters = {}) {
  return useQuery({
    queryKey: [...qk.search(ws ?? null, q, type), filters], queryFn: () => dataApi().search(ws!, q, { ...filters, type: type === 'all' ? undefined : type }),
    enabled: Boolean(ws && q.trim().length >= 2),
  });
}
/** t303u: Show more for one kind: the next pages of 20 after the `start` results already shown. Fetches once `enabled`. */
export function useSearchMore(ws: Ws, q: string, kind: import('@tradeoutlet/shared').SearchKind, filters: SearchFilters, start: number, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...qk.search(ws ?? null, q, kind), filters, 'more', start],
    queryFn: ({ pageParam }) => dataApi().search(ws!, q, { ...filters, type: kind, offset: pageParam, limit: 20 }),
    initialPageParam: start,
    getNextPageParam: (last, pages) => (last.more?.[kind] ? start + pages.reduce((n, p) => n + ((p[kind] as unknown[] | undefined)?.length ?? 0), 0) : undefined),
    enabled: Boolean(ws && enabled && q.trim().length >= 2),
  });
}

// ---- chat list
export function useChatList(ws: Ws) {
  return useQuery({ queryKey: qk.conversations(ws ?? ''), queryFn: () => dataApi().conversations(ws!), enabled: Boolean(ws), refetchInterval: 60_000 });
}

// ---- my shifts and the time clock
export function useMyShifts(ws: Ws, from: string, to: string) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'my-shifts', from, to), queryFn: () => dataApi().wf.myShifts(ws!, { from, to }), enabled: Boolean(ws) });
}
export function useCurrentTime(ws: Ws) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'time-current'), queryFn: () => dataApi().wf.currentTime(ws!), enabled: Boolean(ws), refetchInterval: 60_000 });
}
export function useClockPreview(ws: Ws, locationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.wf(ws ?? '', `clock-preview-${locationId ?? ''}`), queryFn: () => dataApi().wf.clockPreview(ws!, { location_id: locationId }),
    enabled: Boolean(ws && locationId) && enabled, staleTime: 5 * 60_000,
  });
}
/** Every workforce query of the workspace (after a clock event or a shift change). */
export function useInvalidateWorkforce(ws: Ws) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['wf', ws] });
}

// ---- my runs (the driver tab rule)
export function useMyRuns(ws: Ws, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: qk.fleet(ws ?? null, 'journeys', 'mine', from, to), queryFn: () => dataApi().fleet.journeys(ws!, { mine: true, from, to }),
    enabled: Boolean(ws) && enabled, staleTime: 5 * 60_000,
  });
}

// ---- Today (mobile Phase 1)
/** Everything the Today screen shows, in one request. Refetched on focus and every minute (unread chats, who's in). */
export function useMeToday(ws: Ws) {
  return useQuery({ queryKey: qk.today(ws ?? ''), queryFn: () => dataApi().meToday(ws!), enabled: Boolean(ws), refetchInterval: 60_000, refetchOnWindowFocus: true });
}
export type ClockActionInput =
  | { action: 'in'; client_event_id: string; shift_id?: string; location_id?: string; geo: GeoReading | null }
  | { action: 'out'; client_event_id: string; geo: GeoReading | null }
  | { action: 'break' | 'resume' };
/**
 * One clock tap. The location reading and the idempotency id come from the app (browser geolocation / native
 * location; sessionStorage / device storage), so this stays platform-free.
 */
export function useClockAction(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: ClockActionInput) => {
      const w = ws!;
      if (a.action === 'in') return dataApi().wf.clockIn(w, { client_event_id: a.client_event_id, shift_id: a.shift_id, location_id: a.shift_id ? undefined : a.location_id, geo: a.geo });
      if (a.action === 'out') return dataApi().wf.clockOut(w, { client_event_id: a.client_event_id, geo: a.geo });
      return a.action === 'break' ? dataApi().wf.startBreak(w) : dataApi().wf.endBreak(w);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['wf', ws] }),
  });
}

// ---- Shifts (mobile Phase 1). Keys match the web pages' so their invalidations are shared.
const invalidateWf = (qc: ReturnType<typeof useQueryClient>, ws: Ws) => qc.invalidateQueries({ queryKey: ['wf', ws] });
export function useOpenShifts(ws: Ws, from: string, to: string, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'open-shifts'), queryFn: () => dataApi().wf.openShifts(ws!, { from, to }), enabled: Boolean(ws) && enabled });
}
export function useMySwaps(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'swaps', 'mine'), queryFn: () => dataApi().wf.swaps(ws!, { scope: 'mine' }), enabled: Boolean(ws) && enabled });
}
export function useMyShiftOffers(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'offers', 'mine'), queryFn: () => dataApi().wf.shiftOffers(ws!, { scope: 'mine' }), enabled: Boolean(ws) && enabled });
}
/** The read-only Team view (who works where, approved time off) for a date range. */
export function useTeamSchedule(ws: Ws, from: string, to: string, locationId?: string | null, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'team-schedule', from, to, locationId ?? ''), queryFn: () => dataApi().wf.teamSchedule(ws!, { from, to, location_id: locationId ?? undefined }), enabled: Boolean(ws && from && to) && enabled, retry: false });
}
export function useConfirmShifts(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => { for (const id of ids) await dataApi().wf.confirmShift(ws!, id); return ids.length; },
    onSettled: () => invalidateWf(qc, ws),
  });
}
export type ShiftRequestInput = { kind: 'swap' | 'cover' | 'drop'; shift_id: string; target_user_id?: string; target_shift_id?: string; note?: string };
/** Swap (with a colleague's shift), offer (cover: anyone eligible or a named colleague) or drop. */
export function useShiftRequest(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: ShiftRequestInput) => dataApi().wf.createSwap(ws!, b), onSettled: () => invalidateWf(qc, ws) });
}
export function useSwapAction(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (a: { id: string; action: 'accept' | 'decline' | 'cancel' }) => dataApi().wf.swapAction(ws!, a.id, a.action), onSettled: () => invalidateWf(qc, ws) });
}
export function useClaimOpenShift(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (shiftId: string) => dataApi().wf.claimOpenShift(ws!, shiftId), onSettled: () => invalidateWf(qc, ws) });
}

// ---- Time off
export function useLeaveTypes(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'leave-types'), queryFn: () => dataApi().wf.leaveTypes(ws!), enabled: Boolean(ws) && enabled, staleTime: 5 * 60_000 });
}
export function useMyHoliday(ws: Ws, me: string | null | undefined, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'holiday', me ?? '', 'now'), queryFn: () => dataApi().wf.holidayDetail(ws!, me!), enabled: Boolean(ws && me) && enabled });
}
/** t354u: one person's holiday year (balance, absences); yours or someone you approve leave for / manage. */
export function useHolidayDetail(ws: Ws, userId: string | null | undefined, asOf?: string, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'holiday', userId ?? '', asOf ?? 'now'), queryFn: () => dataApi().wf.holidayDetail(ws!, userId!, asOf), enabled: Boolean(ws && userId) && enabled, retry: (n, e) => !(e instanceof Error && 'status' in e && (e as { status: number }).status === 403) && n < 2 });
}
export type LeaveRequestInput = { leave_type_id: string; start_date: string; end_date: string; start_half_day?: boolean; end_half_day?: boolean; reason?: string };
/** t182u: my own time-off requests (Shifts → Requests on the phone). */
export function useMyLeave(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'leave', 'mine'), queryFn: () => dataApi().wf.leaveRequests(ws!, { scope: 'mine' }), enabled: Boolean(ws) && enabled });
}
export function useCancelLeave(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => dataApi().wf.leaveAction(ws!, id, 'cancel'), onSettled: () => invalidateWf(qc, ws) });
}
export function useRequestLeave(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: LeaveRequestInput) => dataApi().wf.requestLeave(ws!, b), onSettled: () => invalidateWf(qc, ws) });
}

/** t537u: people I can add time off for (my leave-approval scope). Only when I approve leave somewhere. */
export function useBookablePeople(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'leave-bookable'), queryFn: () => dataApi().wf.leaveBookable(ws!), enabled: Boolean(ws) && enabled, staleTime: 60_000 });
}
/** t537u: what adding this time off would do (hours, allowance warning, leave already booked, their shifts). */
export function useLeavePreview(ws: Ws, b: LeaveBookingInput | null) {
  return useQuery({ queryKey: qk.wf(ws ?? '', 'leave-preview', b), queryFn: () => dataApi().wf.previewLeave(ws!, b!), enabled: Boolean(ws && b), staleTime: 0, retry: false });
}
/** t537u: add time off for someone (approved now); `shifts` says what happens to their shifts on those days. */
export function useBookLeave(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: LeaveBookingInput) => dataApi().wf.requestLeave(ws!, b), onSettled: () => invalidateWf(qc, ws) });
}

/** t413u: HR changes waiting for my approval. `enabled` only when I can approve somewhere (settings.can_approve or hr.edit). */
export function useHrApprovals(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.hrApprovals(ws ?? ''), queryFn: () => dataApi().hr.approvals(ws!), enabled: Boolean(ws) && enabled, staleTime: 60_000, refetchInterval: 120_000 });
}
/** t413u: people with HR details still to fill in (Missing HR details). */
export function useHrMissing(ws: Ws, enabled = true) {
  return useQuery({ queryKey: qk.hrMissing(ws ?? ''), queryFn: () => dataApi().hr.missing(ws!), enabled: Boolean(ws) && enabled, retry: false });
}
/** t413u: starters and leavers in a date range (YYYY-MM-DD). */
export function useHrStartersLeavers(ws: Ws, from: string, to: string, enabled = true) {
  return useQuery({ queryKey: qk.hrStartersLeavers(ws ?? '', from, to), queryFn: () => dataApi().hr.startersLeavers(ws!, { from, to }), enabled: Boolean(ws) && enabled && Boolean(from && to), retry: false });
}
/** t413u: what erasing a leaver's HR record would remove and keep. */
export function useHrErasure(ws: Ws, userId: string, enabled = true) {
  return useQuery({ queryKey: qk.hrErasure(ws ?? '', userId), queryFn: () => dataApi().hr.erasure(ws!, userId), enabled: Boolean(ws) && enabled, retry: false, staleTime: 0 });
}
